import { convexQuery } from "@convex-dev/react-query";
import { isValidConvexId } from "@plasma/utils/convex-id";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { AlertCircle } from "lucide-react";

import { categoriesGetForMenu } from "~/api-refs";
import { CategoryCard } from "~/components/menus/categories/category-card";
import { CategoriesEmptyState } from "~/components/menus/categories/empty";
import { LoadingPanel } from "~/components/shared/loading-panel";
import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import { toConvexTableId } from "~/lib/convex-table-id";

export const Route = createFileRoute(
  "/_authenticated/menus/$menuId/categories/"
)({
  component: CategoriesListPage,
});

function CategoriesListPage() {
  const { menuId } = Route.useParams();

  // Validate ID format before making any queries
  const validMenuId = isValidConvexId(menuId);

  const { data: categories, isPending } = useQuery(
    convexQuery(
      categoriesGetForMenu,
      validMenuId
        ? {
            menuId: toConvexTableId("menus", menuId),
            includeAvailabilityTimes: true,
          }
        : "skip"
    )
  );

  if (!validMenuId) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Menu Not Found</AlertTitle>
        <AlertDescription className="flex flex-col gap-3">
          <span>
            The menu ID in the URL is invalid or the menu doesn't exist.
          </span>
          <Button asChild className="w-fit" size="sm" variant="outline">
            <Link to="/menus">Back to Menus</Link>
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  if (isPending || !categories) {
    return <LoadingPanel message="Loading categories..." />;
  }

  if (categories.length === 0 || !categories[0]) {
    return <CategoriesEmptyState />;
  }

  return (
    <div
      className="grid grid-cols-[repeat(auto-fill,minmax(13rem,1fr))] gap-4"
      id="categoriesContainer"
    >
      {categories.map((category) => (
        <CategoryCard category={category} key={category.id} menuId={menuId} />
      ))}
    </div>
  );
}
